import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { Translate } from "@material-ui/icons";
import { css } from "@emotion/css";
import { Controller, useFormContext } from "react-hook-form";
import { LESSON_STATES, LessonState, TARGET_JOINTS, TargetJoint } from "../constants";
import { LessonFormData } from "../types";
import { useAutoTranslateAll } from "../hooks/useAutoTranslateAll";
import { useActiveLocale } from "../ActiveLocaleContext";
import { useTranslation } from "react-i18next";
import CreatorLabel from "../../../components/CreatorLabel";

type Props = {
  isEditing: boolean;
  onChangeState: (state: LessonState) => void;
  disabled?: boolean;
};

const LessonMetadataSection: React.VFC<Props> = ({ isEditing, onChangeState, disabled }) => {
  const { t } = useTranslation();
  const form = useFormContext<LessonFormData>();
  const { control, watch } = form;
  const state = watch("state");
  const creatorId = watch("creator_id");
  const { activeLocale } = useActiveLocale();
  const { translateAll, isTranslating, progress } = useAutoTranslateAll(form, activeLocale);

  return (
    <Box className={styles.wrap}>
      {isEditing && (
        <Box mb={2}>
          <Typography variant="h6">{t("creator.title")}</Typography>
          <Typography variant="body1">
            <CreatorLabel creatorId={creatorId} />
          </Typography>
        </Box>
      )}

      <Typography variant="h5" gutterBottom>
        Lesson metadata
      </Typography>

      <Controller
        control={control}
        name="target_joints"
        render={({ onChange, value }) => (
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Target joints</InputLabel>
            <Select
              multiple
              label="Target joints"
              value={(value as TargetJoint[]) ?? []}
              onChange={e => onChange(e.target.value as TargetJoint[])}
              renderValue={selected => (
                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                  {(selected as string[]).map(v => (
                    <Chip key={v} label={v} size="small" />
                  ))}
                </Box>
              )}
            >
              {TARGET_JOINTS.map(j => (
                <MenuItem key={j} value={j}>
                  {j}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        {isEditing && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>State</InputLabel>
            <Select
              label="State"
              value={state ?? "draft"}
              onChange={e => onChangeState(e.target.value as LessonState)}
              disabled={disabled}
            >
              {LESSON_STATES.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={isTranslating ? <CircularProgress size={14} /> : <Translate />}
          onClick={translateAll}
          disabled={isTranslating}
        >
          {isTranslating ? `Translating ${progress.done}/${progress.total}...` : "Autotranslate missing translations"}
        </Button>
      </Box>
    </Box>
  );
};

export default LessonMetadataSection;

const styles = {
  wrap: css`
    margin-bottom: 16px;
  `,
};
